import {
  createHomeExerciseProgramRequestSchema,
  createPlanOfCareRequestSchema,
  createVisitRequestSchema,
  homeExerciseProgramEnvelopeSchema,
  homeExerciseProgramListEnvelopeSchema,
  outcomeMeasureEnvelopeSchema,
  outcomeMeasureListEnvelopeSchema,
  planOfCareEnvelopeSchema,
  planOfCareListEnvelopeSchema,
  recordOutcomeMeasureRequestSchema,
  updatePlanOfCareRequestSchema,
  visitEnvelopeSchema,
  visitListEnvelopeSchema,
  type CreateHomeExerciseProgramRequest,
  type CreatePlanOfCareRequest,
  type CreateVisitRequest,
  type HomeExerciseProgramResponse,
  type OutcomeMeasureResponse,
  type PlanOfCareResponse,
  type RecordOutcomeMeasureRequest,
  type UpdatePlanOfCareRequest,
  type VisitResponse,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The physical therapy & rehab specialty-module API surface (`BACKLOG.md` M6), validated against
 * `@emr/shared`'s own request/response schemas — the exact same schemas `server/src/physicalTherapy/
 * routes.ts` validates request bodies with, matching `api/dermatology.ts`/`api/aesthetics.ts`'s own
 * reasoning for why this cannot silently drift from what the server accepts and returns.
 */

export type {
  CreateHomeExerciseProgramRequest,
  CreatePlanOfCareRequest,
  CreateVisitRequest,
  HomeExerciseProgramResponse,
  OutcomeMeasureResponse,
  PlanOfCareResponse,
  RecordOutcomeMeasureRequest,
  UpdatePlanOfCareRequest,
  VisitResponse,
};

function pt(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/physical-therapy${suffix}`;
}

export async function fetchPlansOfCare(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PlanOfCareResponse[]> {
  const response = await apiRequest(
    pt(patientId, '/plans-of-care'),
    planOfCareListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.plansOfCare;
}

export async function createPlanOfCare(
  patientId: string,
  input: CreatePlanOfCareRequest,
): Promise<PlanOfCareResponse> {
  const response = await apiRequest(pt(patientId, '/plans-of-care'), planOfCareEnvelopeSchema, {
    method: 'POST',
    body: createPlanOfCareRequestSchema.parse(input),
  });
  return response.planOfCare;
}

export async function updatePlanOfCare(
  patientId: string,
  pocId: string,
  input: UpdatePlanOfCareRequest,
): Promise<PlanOfCareResponse> {
  const response = await apiRequest(
    pt(patientId, `/plans-of-care/${pocId}`),
    planOfCareEnvelopeSchema,
    { method: 'PATCH', body: updatePlanOfCareRequestSchema.parse(input) },
  );
  return response.planOfCare;
}

export async function fetchVisits(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly VisitResponse[]> {
  const response = await apiRequest(
    pt(patientId, '/visits'),
    visitListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.visits;
}

export async function createVisit(
  patientId: string,
  input: CreateVisitRequest,
): Promise<VisitResponse> {
  const response = await apiRequest(pt(patientId, '/visits'), visitEnvelopeSchema, {
    method: 'POST',
    body: createVisitRequestSchema.parse(input),
  });
  return response.visit;
}

export async function fetchHomeExercisePrograms(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly HomeExerciseProgramResponse[]> {
  const response = await apiRequest(
    pt(patientId, '/home-exercise-programs'),
    homeExerciseProgramListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.programs;
}

export async function createHomeExerciseProgram(
  patientId: string,
  input: CreateHomeExerciseProgramRequest,
): Promise<HomeExerciseProgramResponse> {
  const response = await apiRequest(
    pt(patientId, '/home-exercise-programs'),
    homeExerciseProgramEnvelopeSchema,
    { method: 'POST', body: createHomeExerciseProgramRequestSchema.parse(input) },
  );
  return response.program;
}

export async function fetchOutcomeMeasures(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly OutcomeMeasureResponse[]> {
  const response = await apiRequest(
    pt(patientId, '/outcome-measures'),
    outcomeMeasureListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.measures;
}

export async function recordOutcomeMeasure(
  patientId: string,
  input: RecordOutcomeMeasureRequest,
): Promise<OutcomeMeasureResponse> {
  const response = await apiRequest(
    pt(patientId, '/outcome-measures'),
    outcomeMeasureEnvelopeSchema,
    { method: 'POST', body: recordOutcomeMeasureRequestSchema.parse(input) },
  );
  return response.measure;
}
